import React, { useEffect, useState } from 'react';
import { signInWithEmailAndPassword, signOut, onAuthStateChanged, createUserWithEmailAndPassword, updatePassword } from 'firebase/auth';
import { auth } from '../firebase';
import { fetchSiteContent, saveSiteContent } from '../services/firestoreService';
import { LogOut, Mail, Phone, Trash2, Save, RefreshCw, KeyRound, Inbox, Briefcase, FileEdit, ShieldCheck, Sun, Activity, HardHat, ExternalLink, ChevronDown, ChevronUp, Star } from 'lucide-react';
import { HERO_SLIDES, STATS, FOCUS_AREAS, WHY_TES, SOLUTIONS, TESTIMONIALS } from '../data/content';

const API = process.env.REACT_APP_BACKEND_URL;
const ADMIN_TOKEN = 'TES@Admin2026'; // matches backend default; rotate via env if needed
const ADMIN_EMAIL = 'solutions@tricoliteenergy.in';
const DEFAULT_PW = 'TRICOLITE@12345';

function Login({ onIn }) {
  const [email, setEmail] = useState('');
  const [pw, setPw] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [info, setInfo] = useState('');

  const tryLogin = async (e) => {
    e.preventDefault(); setBusy(true); setErr(''); setInfo('');
    try {
      await signInWithEmailAndPassword(auth, email, pw);
      onIn();
    } catch (e1) {
      if ((e1.code === 'auth/user-not-found' || e1.code === 'auth/invalid-credential') && pw === DEFAULT_PW && email === ADMIN_EMAIL) {
        try {
          await createUserWithEmailAndPassword(auth, email, pw);
          setInfo('Admin account created! You are now logged in.');
          await signInWithEmailAndPassword(auth, email, pw);
          onIn();
        } catch (e2) {
          if (e2.code === 'auth/email-already-in-use') {
            setErr('Account exists but password is wrong. Contact your developer to reset.');
          } else { setErr(e2.message); }
        }
      } else { setErr(e1.message); }
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[#0E1620] text-white flex items-center justify-center px-4">
      <div className="absolute inset-0 grid-bg-dark opacity-40 pointer-events-none"/>
      <form onSubmit={tryLogin} className="relative w-full max-w-md bg-white/[0.04] border border-white/10 backdrop-blur-xl rounded-3xl p-8 md:p-10">
        <img src="/logos/full.png" alt="Tricolite" className="h-12 w-auto mb-6" style={{filter:'brightness(1.15)'}}/>
        <div className="text-[11px] uppercase tracking-[0.22em] text-[#F3763A] font-semibold mb-2">Admin Console</div>
        <h1 className="font-display font-bold text-[28px] mb-6" style={{color:'#fff'}}>Sign in</h1>
        <label className="block mb-3"><span className="block text-[11px] uppercase tracking-wider text-white/50 mb-1">Email</span>
          <input value={email} onChange={(e)=>setEmail(e.target.value)} className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 focus:outline-none focus:border-[#2596BE]"/>
        </label>
        <label className="block mb-5"><span className="block text-[11px] uppercase tracking-wider text-white/50 mb-1">Password</span>
          <input type="password" value={pw} onChange={(e)=>setPw(e.target.value)} className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 focus:outline-none focus:border-[#2596BE]"/>
        </label>
        {err && <div className="text-[13px] text-red-300 mb-4">{err}</div>}
        {info && <div className="text-[13px] text-green-300 mb-4">{info}</div>}
        <button disabled={busy} className="w-full bg-[#2596BE] hover:bg-[#F3763A] disabled:opacity-60 transition-colors text-white py-3.5 rounded-full font-medium">{busy ? 'Signing in…' : 'Sign In'}</button>
      </form>
    </div>
  );
}

function ExpandRow({ label, value }) {
  if (value === undefined || value === null || value === '' || value === '—') return null;
  return <div className="py-2 border-t border-[#E1DCCF]"><span className="text-[11px] uppercase tracking-wider text-[#8A8F97] block mb-1">{label}</span><div className="text-[13px] text-[#0E1620] whitespace-pre-wrap">{typeof value === 'object' && !React.isValidElement(value) ? JSON.stringify(value, null, 2) : value}</div></div>;
}

function formatWhen(createdAt) {
  if (!createdAt) return '—';
  // Firestore Timestamp object
  if (createdAt?.toDate) return createdAt.toDate().toLocaleString();
  // ISO string from MongoDB
  if (typeof createdAt === 'string') {
    const d = new Date(createdAt);
    return isNaN(d.getTime()) ? createdAt : d.toLocaleString();
  }
  return String(createdAt);
}

function DetailCard({ item, fields, onDelete }) {
  const [open, setOpen] = useState(false);
  const when = formatWhen(item.createdAt);
  return (
    <div className="border border-[#E1DCCF] rounded-2xl bg-white overflow-hidden">
      <div className="flex items-center gap-4 p-4 cursor-pointer hover:bg-[#F6F2EB] transition-colors" onClick={() => setOpen(!open)}>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3 flex-wrap">
            <span className="font-display font-bold text-[15px] text-[#0E1620]">{fields.title(item)}</span>
            <span className="text-[11px] text-[#8A8F97]">{when}</span>
          </div>
          <div className="text-[13px] text-[#4C5560] mt-0.5 truncate">{fields.subtitle(item)}</div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {onDelete && <button onClick={(e) => { e.stopPropagation(); if(window.confirm('Delete this entry?')) onDelete(item.id); }} className="w-8 h-8 rounded-full flex items-center justify-center text-red-400 hover:bg-red-50 hover:text-red-600"><Trash2 className="w-4 h-4"/></button>}
          {open ? <ChevronUp className="w-4 h-4 text-[#8A8F97]"/> : <ChevronDown className="w-4 h-4 text-[#8A8F97]"/>}
        </div>
      </div>
      {open && (
        <div className="px-4 pb-4 space-y-0">
          {fields.details(item).map(([label, val], i) => <ExpandRow key={i} label={label} value={val}/>)}
        </div>
      )}
    </div>
  );
}

export default function Admin() {
  const [user, setUser] = useState(null);
  const [ready, setReady] = useState(false);
  const [tab, setTab] = useState('enquiries');
  const [enq, setEnq] = useState([]);
  const [car, setCar] = useState([]);
  const [solar, setSolar] = useState([]);
  const [energy, setEnergy] = useState([]);
  const [subs, setSubs] = useState([]);
  const [csat, setCsat] = useState([]);
  const [content, setContent] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [newPw, setNewPw] = useState('');

  useEffect(() => onAuthStateChanged(auth, (u) => { setUser(u); setReady(true); }), []);

  const load = async () => {
    setBusy(true);
    try {
      const res = await fetch(`${API}/api/admin/submissions?token=${encodeURIComponent(ADMIN_TOKEN)}`);
      const data = await res.json();
      if (data.error) throw new Error(data.error);
      setEnq(data.enquiries || []);
      setSolar(data.solar_surveys || []);
      setEnergy(data.energy_audits || []);
      setSubs(data.subcontractor_registrations || []);
      setCsat(data.csat_surveys || []);
      // careers + site content still come from firestore (best-effort)
      try {
        const s = await fetchSiteContent();
        setContent(s || { hero: HERO_SLIDES, stats: STATS, focus: FOCUS_AREAS, why: WHY_TES, solutions: SOLUTIONS, testimonials: TESTIMONIALS });
      } catch (e) {
        setContent({ hero: HERO_SLIDES, stats: STATS, focus: FOCUS_AREAS, why: WHY_TES, solutions: SOLUTIONS, testimonials: TESTIMONIALS });
      }
    } catch (er) { setMsg('Load error: ' + er.message); }
    finally { setBusy(false); }
  };
  useEffect(() => { if (user) load(); }, [user]);

  const deleteSubmission = async (collection, id) => {
    try {
      await fetch(`${API}/api/admin/submission/${collection}/${id}?token=${encodeURIComponent(ADMIN_TOKEN)}`, { method: 'DELETE' });
      load();
    } catch (e) { setMsg('Delete failed: ' + e.message); }
  };

  const save = async () => {
    setBusy(true); setMsg('');
    try { await saveSiteContent(content); setMsg('Saved.'); }
    catch (e) { setMsg('Save error: ' + e.message); }
    finally { setBusy(false); setTimeout(() => setMsg(''), 3500); }
  };

  const changePw = async () => {
    if (!newPw || newPw.length < 6) { setMsg('Password must be at least 6 characters.'); return; }
    setBusy(true);
    try { await updatePassword(user, newPw); setMsg('Password changed.'); setNewPw(''); }
    catch (e) { setMsg('Could not change password: ' + e.message); }
    finally { setBusy(false); }
  };

  if (!ready) return <div className="min-h-screen bg-[#0E1620] flex items-center justify-center text-white">Loading…</div>;
  if (!user) return <Login onIn={() => {}} />;

  const TABS = [
    { id: 'enquiries', label: 'Enquiries', icon: Inbox, count: enq.length },
    { id: 'csat', label: 'CSat Surveys', icon: Star, count: csat.length },
    { id: 'solar', label: 'Solar Surveys', icon: Sun, count: solar.length },
    { id: 'energy', label: 'Energy Audits', icon: Activity, count: energy.length },
    { id: 'subcontractors', label: 'Subcontractors', icon: HardHat, count: subs.length },
    { id: 'content', label: 'Site Content', icon: FileEdit },
    { id: 'security', label: 'Setup & Security', icon: ShieldCheck },
  ];

  return (
    <div className="min-h-screen bg-[#F6F2EB]">
      <header className="bg-[#0E1620] text-white px-6 md:px-10 py-4 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <img src="/logos/full.png" alt="Tricolite" className="h-9 w-auto"/>
          <span className="text-[11px] uppercase tracking-[0.22em] text-[#F3763A]">Admin Console</span>
        </div>
        <div className="flex items-center gap-3 text-[13px]">
          <span className="opacity-60">{user.email}</span>
          <button onClick={() => signOut(auth)} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 hover:bg-[#F3763A]"><LogOut className="w-4 h-4"/> Sign out</button>
        </div>
      </header>

      <div className="px-6 md:px-10 py-8 max-w-[1400px] mx-auto">
        <div className="flex flex-wrap gap-2 mb-8">
          {TABS.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)} className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-full text-[13px] font-medium border transition-colors ${tab===t.id ? 'bg-[#2596BE] text-white border-[#2596BE]' : 'bg-white border-[#E1DCCF] text-[#0E1620] hover:border-[#2596BE]'}`}>
              <t.icon className="w-4 h-4"/>{t.label}
              {t.count !== undefined && <span className={`text-[11px] px-1.5 py-0.5 rounded-full ${tab===t.id ? 'bg-white/20' : 'bg-[#ECE4D2]'}`}>{t.count}</span>}
            </button>
          ))}
          <button onClick={load} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-white border border-[#E1DCCF] text-[13px] hover:border-[#2596BE]"><RefreshCw className={`w-4 h-4 ${busy?'animate-spin':''}`}/> Refresh</button>
        </div>

        {msg && <div className="mb-5 p-3 rounded-xl bg-[#2596BE]/10 border border-[#2596BE]/30 text-[13px]">{msg}</div>}

        {/* ENQUIRIES */}
        {tab === 'enquiries' && (
          <div className="space-y-3">
            {enq.length === 0 ? <Empty text="No enquiries yet."/> : enq.map(e => (
              <DetailCard key={e.id} item={e} onDelete={(id) => deleteSubmission('enquiries', id)} fields={{
                title: (e) => e.name || '—',
                subtitle: (e) => `${e.company || '—'} · ${e.email}`,
                details: (e) => [
                  ['Name', e.name], ['Email', e.email], ['Phone', e.phone], ['Company', e.company],
                  ['Message', e.message],
                  ['RFQ File', e.rfqFileName], ['RFQ URL', e.rfqUrl && <a href={e.rfqUrl} target="_blank" rel="noreferrer" className="text-[#2596BE] inline-flex items-center gap-1">Download <ExternalLink className="w-3 h-3"/></a>],
                ],
              }}/>
            ))}
          </div>
        )}

        {/* CSAT SURVEYS */}
        {tab === 'csat' && (
          <div className="space-y-3">
            {csat.length === 0 ? <Empty text="No customer satisfaction surveys yet."/> : csat.map(c => {
              const r = c.respondent || {};
              const rec = c.recommend || {};
              const ratings = c.ratings || {};
              return (
                <DetailCard key={c.id} item={c} onDelete={(id) => deleteSubmission('csat_surveys', id)} fields={{
                  title: (it) => `${r.name || '—'} — ${r.organisation || '—'}`,
                  subtitle: (it) => `Overall: ${it.overall ?? '—'}/10  ·  ${r.designation || '—'}  ·  ${r.date || '—'}`,
                  details: () => [
                    ['Respondent', `${r.name || '—'} · ${r.designation || '—'} · ${r.organisation || '—'}`],
                    ['Overall Experience', c.overall != null ? `${c.overall} / 10` : '—'],
                    ...Object.entries(ratings).map(([k, v]) => [k, v != null ? `${v} / 10` : '—']),
                    ['Recommend Us To', (rec.name || rec.contact || rec.organisation) ? `${rec.name || '—'} (${rec.organisation || '—'}) — ${rec.contact || '—'}` : '—'],
                    ['Improvement Inputs', c.improvement],
                  ],
                }}/>
              );
            })}
          </div>
        )}

        {/* SOLAR SURVEYS */}
        {tab === 'solar' && (
          <div className="space-y-3">
            {solar.length === 0 ? <Empty text="No solar surveys yet."/> : solar.map(s => (
              <DetailCard key={s.id} item={s} onDelete={(id) => deleteSubmission('solar_surveys', id)} fields={{
                title: (s) => s.org?.name || '—',
                subtitle: (s) => `${s.org?.location || '—'} · ${s.visit?.representative || '—'}`,
                details: (s) => [
                  ['Organization', s.org?.name], ['Location', s.org?.location],
                  ['Lat / Long', `${s.org?.lat || '—'}, ${s.org?.lng || '—'}`],
                  ['Contacts', s.contacts?.map(c => `${c.name} — ${c.mobile} — ${c.email} (KDM: ${c.kdm})`).join('\n')],
                  ['On Grid', s.systems?.onGrid], ['Off Grid', s.systems?.offGrid], ['Hybrid', s.systems?.hybrid],
                  ['Key Concerns', s.concerns],
                  ['CAD File', `When: ${s.nextSteps?.cadWhen || '—'} · By: ${s.nextSteps?.cadBy || '—'}`],
                  ['Site Photos', `When: ${s.nextSteps?.photoWhen || '—'} · By: ${s.nextSteps?.photoBy || '—'}`],
                  ['Visit Date', s.visit?.date], ['Face Time', s.visit?.faceTime ? `${s.visit.faceTime} mins` : '—'],
                  ['TES Representative', s.visit?.representative],
                ],
              }}/>
            ))}
          </div>
        )}

        {/* ENERGY AUDITS */}
        {tab === 'energy' && (
          <div className="space-y-3">
            {energy.length === 0 ? <Empty text="No energy audit requests yet."/> : energy.map(e => (
              <DetailCard key={e.id} item={e} onDelete={(id) => deleteSubmission('energy_audits', id)} fields={{
                title: (e) => e.org?.name || '—',
                subtitle: (e) => `${e.org?.city || '—'} · ${e.declaration?.name || '—'}`,
                details: (e) => [
                  ['Organisation', e.org?.name], ['City', e.org?.city],
                  ['Products', e.org?.products], ['Plant Capacity', e.org?.plantCapacity],
                  ['Sanctioned Demand', e.org?.sanctionedDemand], ['Connected Load', e.org?.connectedLoad],
                  ['Peak Demand', e.org?.peakDemand], ['Lighting Load', e.org?.lightingLoad],
                  ['Separate Feeder', e.org?.separateFeeder], ['APFC Installed', e.org?.apfcInstalled],
                  ['Team', e.team?.map(t => `${t.name} — ${t.designation} — ${t.mobile} — ${t.email}`).join('\n')],
                  ['Elec Rate/kWh', e.elecRate?.ratePerKwh], ['Elec Annual Cost', e.elecRate?.totalAnnualCost],
                  ['Thermal Rate/kWh', e.thermalRate?.ratePerKwh], ['Thermal Annual Cost', e.thermalRate?.totalAnnualCost],
                  ['Transformers', e.transformers?.map(t => `${t.make} ${t.type} ${t.kvaRating} KVA (${t.loading}%)`).join('\n')],
                  ['Past Audit Done', e.pastAudit?.everDone],
                  ['Key Concerns', e.concerns],
                  ['Preferred Audit Date', `${e.futureAudit?.month || ''} ${e.futureAudit?.year || ''} — ${e.futureAudit?.dates || ''}`],
                  ['Declared By', `${e.declaration?.name || '—'} · ${e.declaration?.designation || '—'}`],
                ],
              }}/>
            ))}
          </div>
        )}

        {/* SUBCONTRACTORS */}
        {tab === 'subcontractors' && (
          <div className="space-y-3">
            {subs.length === 0 ? <Empty text="No subcontractor registrations yet."/> : subs.map(s => (
              <DetailCard key={s.id} item={s} onDelete={(id) => deleteSubmission('subcontractor_registrations', id)} fields={{
                title: (s) => s.companyName || '—',
                subtitle: (s) => `${s.ownerName || '—'} · ${s.partnerEmail || '—'}`,
                details: (s) => [
                  ['Company', s.companyName], ['GSTIN', s.gstinNumber], ['Bank Details', s.bankDetails],
                  ['Owner', s.ownerName], ['Structure', s.orgStructure], ['Established', s.establishedIn], ['Type', s.companyType],
                  ['Contact', s.partnerName], ['Mobile', s.partnerMobile], ['Email', s.partnerEmail], ['Address', s.partnerAddress],
                  ['Escalation Matrix', s.escalationMatrix],
                  ['Permanent Employees', s.permanentEmployees], ['Contractual Employees', s.contractualEmployees],
                  ['References', s.references], ['Tools & Tackle', s.toolsAndTackle],
                  ['PF/ESIC', s.pfEsic], ['Past TES Experience', s.pastExperience],
                  ['QC/EHS Recommendation', s.qcRecommendation], ['PM Recommendation', s.pmRecommendation],
                  ['Documents', s.documents && Object.entries(s.documents).map(([k, v]) => (
                    <div key={k} className="mb-1"><a href={v.url} target="_blank" rel="noreferrer" className="text-[#2596BE] inline-flex items-center gap-1 text-[13px]"><ExternalLink className="w-3 h-3"/>{v.name}</a></div>
                  ))],
                ],
              }}/>
            ))}
          </div>
        )}

        {/* CAREERS removed — not in use */}

        {/* SITE CONTENT */}
        {tab === 'content' && content && (
          <div className="space-y-8">
            <Section title="Hero Slides" onSave={save} busy={busy}>
              {content.hero?.map((h, i) => (
                <div key={i} className="grid md:grid-cols-4 gap-3 mb-3">
                  <Input v={h.eyebrow} on={(v)=>updArr(setContent,content,'hero',i,'eyebrow',v)} ph="Eyebrow"/>
                  <Input v={h.title} on={(v)=>updArr(setContent,content,'hero',i,'title',v)} ph="Title"/>
                  <Input v={h.accent} on={(v)=>updArr(setContent,content,'hero',i,'accent',v)} ph="Accent"/>
                  <Input v={h.desc} on={(v)=>updArr(setContent,content,'hero',i,'desc',v)} ph="Description"/>
                </div>
              ))}
            </Section>
            <Section title="Stats" onSave={save} busy={busy}>
              {content.stats?.map((s, i) => (
                <div key={i} className="grid md:grid-cols-3 gap-3 mb-3">
                  <Input v={s.value} on={(v)=>updArr(setContent,content,'stats',i,'value',v)} ph="Value"/>
                  <Input v={s.suffix} on={(v)=>updArr(setContent,content,'stats',i,'suffix',v)} ph="Suffix"/>
                  <Input v={s.label} on={(v)=>updArr(setContent,content,'stats',i,'label',v)} ph="Label"/>
                </div>
              ))}
            </Section>
          </div>
        )}

        {/* SETUP & SECURITY */}
        {tab === 'security' && (
          <div className="grid lg:grid-cols-2 gap-6">
            <div className="bg-white border border-[#E1DCCF] rounded-2xl p-8">
              <h3 className="font-display font-bold text-[22px] mb-3 heading-ink">Change Password</h3>
              <p className="text-[13px] text-[#4C5560] mb-5">Recommend 12+ chars, mixed case + numbers + symbols.</p>
              <div className="flex gap-3">
                <input type="password" value={newPw} onChange={(e)=>setNewPw(e.target.value)} placeholder="New password" className="flex-1 cf-input"/>
                <button onClick={changePw} disabled={busy} className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-[#2596BE] text-white hover:bg-[#F3763A] disabled:opacity-60"><KeyRound className="w-4 h-4"/>Update</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Empty({ text }) {
  return <div className="bg-white border border-[#E1DCCF] rounded-2xl p-12 text-center text-[#8A8F97] text-[15px]">{text}</div>;
}

function Section({ title, children, onSave, busy }) {
  return (
    <section className="bg-white border border-[#E1DCCF] rounded-2xl p-6 md:p-8">
      <div className="flex items-center justify-between mb-5">
        <h3 className="font-display font-bold text-[20px] heading-ink">{title}</h3>
        <button onClick={onSave} disabled={busy} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#2596BE] text-white text-[13px] hover:bg-[#F3763A] disabled:opacity-60"><Save className="w-4 h-4"/>Save</button>
      </div>
      {children}
    </section>
  );
}

function updArr(setContent, content, key, idx, field, val) {
  const arr = [...content[key]];
  arr[idx] = { ...arr[idx], [field]: val };
  setContent({ ...content, [key]: arr });
}

function Input({ v, on, ph }) { return <input value={v||''} onChange={(e)=>on(e.target.value)} placeholder={ph} className="cf-input"/>; }
